// Workpaper page (Workspace 2, screen 8): breadcrumb, title + ref + type, the engine's status, the sign-off tracker
// (Prepared / Review / Partner / EQR with who and when), a "Needs attention" strip (your approval, agent question, blocked,
// flagged issue — the Action center items on this workpaper, each with its action), "How the data flows" (inputs → work →
// result → feeds, from the engine's dependency graph, the client documents that feed it and its sources), the procedure steps
// and the rendered file. The chat panel beside it is this workpaper's conversation. Procedures, answers, review notes and the
// sign-off acts themselves stay one click away under "Engine details".
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { EmptyState, Icon, LoadingRows, StatusPill } from "../../components";
import type { AttentionItem, WpIndexRow, WpSignoffMark } from "../../data/types";
import { fmtDate } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { phaseName, wpTypeLabel } from "../../lib/workspace";
import { useEngagement } from "../engagement/Layout";
import { AttentionIssueChip, EngineDetails, ItemAction, ProgressBar, Sources, useWs, useWsAct } from "./common";
import { subfolderOf } from "./Folders";

export function WsWorkpaper() {
  const { ref = "" } = useParams();
  const key = decodeURIComponent(ref);
  const ws = useWs();
  const { model } = useEngagement();
  const w = ws.wp.find((x) => x.ref === key) ?? ws.wp.find((x) => x.workpaperId === key);
  const engine = model.workpapers.find((x) => x.id === (w?.workpaperId ?? key));
  if (ws.loading) return <LoadingRows rows={6} label="Loading the workpaper" />;
  if (!w && !engine) return <EmptyState title={`No workpaper ${key} in this engagement.`}><Link className="text-brand underline" to={`/e/${ws.id}/workpapers`}>All workpapers</Link></EmptyState>;
  if (!w) {
    // Opened in the engine but not in the index yet (the engine publishes wp_index with its next push).
    return (
      <div className="space-y-3">
        <h1 className="text-xl font-semibold"><span className="font-mono text-muted">{engine!.id}</span> {engine!.title}</h1>
        <StatusPill state={engine!.state} label={engine!.stateLabel} />
        <p className="text-sm text-muted">This workpaper is not in the engagement index yet. <Link className="text-brand hover:underline" to={`/e/${ws.id}/details/wp/${engine!.id}`}>Open it in engine details</Link>.</p>
      </div>
    );
  }
  return <Page w={w} />;
}

function Page({ w }: { w: WpIndexRow }) {
  const ws = useWs();
  const { model } = useEngagement();
  const { ds } = useApp();
  const { open, drawer } = useWsAct(ws.reload);
  const engine = w.workpaperId ? model.workpapers.find((x) => x.id === w.workpaperId) : undefined;
  const procs = useLoad(() => (w.workpaperId ? ds.table(ws.id, "procedures") : Promise.resolve([])), [ds, ws.id, w.workpaperId]);
  const files = useLoad(() => (w.workpaperId ? ds.table(ws.id, "wp_files") : Promise.resolve([])), [ds, ws.id, w.workpaperId]);
  const file = (files.data ?? []).find((f) => f.workpaper === w.workpaperId);
  const url = useLoad(() => (file ? ds.fileUrl(ws.id, file.storagePath) : Promise.resolve(null)), [ds, ws.id, file?.storagePath]);
  const steps = (procs.data ?? []).filter((p) => p.workpaper === w.workpaperId);
  const items = ws.attention.filter((a) => a.ref === w.ref || (!!w.workpaperId && a.workpaperId === w.workpaperId));
  const byRef = (id: string) => ws.wp.find((x) => x.workpaperId === id);
  const sub = subfolderOf(w);

  // How the data flows — every piece is published: the engine's needs/consumers, the client documents that name this ref, sources.
  const inputs: Array<{ label: string; to?: string; note?: string }> = [
    ...(engine?.needs ?? []).map((n) => { const r = byRef(n.dependsOn); return { label: r ? `${r.ref} ${r.name}` : n.dependsOn, to: r ? `/e/${ws.id}/wp/${encodeURIComponent(r.ref)}` : undefined, note: n.data ?? undefined }; }),
    ...ws.pbc.filter((d) => d.neededBy.includes(w.ref)).map((d) => ({ label: `${d.ref} ${d.title}`, to: `/e/${ws.id}/documents#pbc-${d.id}`, note: d.status === "processed" ? "processed" : d.status.replace(/_/g, " ") })),
    ...uniq([...items, ...ws.messages.filter((m) => m.ref === w.ref)].flatMap((x) => x.sources).filter((s) => s.kind === "tb").map((s) => s.label)).map((l) => ({ label: l, note: "trial balance" })),
  ];
  if (w.waitingOn?.kind === "workpaper" && !inputs.some((i) => i.label.startsWith(w.waitingOn!.ref))) {
    inputs.push({ label: `${w.waitingOn.ref} ${w.waitingOn.label}`, to: `/e/${ws.id}/wp/${encodeURIComponent(w.waitingOn.ref)}`, note: "waiting on it" });
  }
  const feeds = uniq(engine?.consumers ?? []).map((c) => { const r = byRef(c); return { label: r ? `${r.ref} ${r.name}` : c, to: r ? `/e/${ws.id}/wp/${encodeURIComponent(r.ref)}` : undefined }; });

  const strip: Array<{ title: string; icon: Parameters<typeof Icon>[0]["name"]; tone: string; list: AttentionItem[] }> = [
    { title: "Your approval", icon: "check", tone: "text-waiting", list: items.filter((a) => a.tab === "approval") },
    { title: "Agent question", icon: "help", tone: "text-agent", list: items.filter((a) => a.tab === "question") },
    { title: "Blocked", icon: "alert", tone: "text-blocked", list: items.filter((a) => a.tab === "blocked" || a.tab === "client_request") },
    { title: "Flagged issue", icon: "flag", tone: "text-blocked", list: items.filter((a) => a.tab === "issue") },
  ];

  return (
    <div className="space-y-5">
      <nav aria-label="Breadcrumb" className="text-xs text-muted">
        <Link className="hover:underline" to={`/e/${ws.id}`}>{ws.header.clientName}</Link> / <Link className="hover:underline" to={`/e/${ws.id}/folders/${w.phase}`}>{w.phase} {phaseName(w.phase)}</Link>
        {sub && <> / <Link className="hover:underline" to={`/e/${ws.id}/folders/${w.phase}/${encodeURIComponent(sub)}`}>{sub}</Link></>} / <span className="font-mono">{w.ref}</span>
      </nav>
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">{w.name}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono text-muted">{w.ref}</span><span className="text-muted">· {wpTypeLabel(w.type)}{w.area ? ` · ${w.area}` : ""}{w.owner ? ` · owner ${w.owner}` : ""}</span>
          <StatusPill state={w.status} label={w.statusLabel} />
          {w.stale && <StatusPill kind="stale" label="Out of date" title={w.staleWhy ?? undefined} />}
        </div>
        {w.statusWhy && !w.waitingOn && <p className="text-sm text-muted">Why: {w.statusWhy}</p>}
        {w.waitingOn && <p className="text-sm text-waiting">Waiting on {w.waitingOn.kind === "client" ? "the client" : w.waitingOn.kind === "workpaper" ? "another workpaper" : "a decision"}: <span className="font-mono">{w.waitingOn.ref}</span> {w.waitingOn.label}</p>}
        {w.stale && w.staleWhy && <p className="text-sm text-stale">Out of date: {w.staleWhy}</p>}
      </header>

      <section aria-labelledby="so-h" className="rounded-lg border border-line bg-raised px-4 py-3">
        <h2 id="so-h" className="mb-2 text-sm font-semibold">Sign-offs</h2>
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {([["Prepared", w.signoffs.preparer], ["Review", w.signoffs.reviewer], ["Partner", w.signoffs.partner], ["EQR", w.signoffs.eqr]] as Array<[string, WpSignoffMark | null]>)
            .filter(([, m]) => m).map(([label, m]) => (
              <li key={label} className={`rounded-md border px-3 py-2 ${m!.done ? "border-done-line bg-done-bg" : "border-line"}`} aria-label={`${label}: ${m!.done ? `${m!.who ?? "signed"}${m!.at ? `, ${fmtDate(m!.at)}` : ""}` : "not yet"}`}>
                <p className="flex items-center gap-1.5 text-xs font-semibold">{m!.done ? <span className="text-done"><Icon name="check" size={14} /></span> : <span aria-hidden="true" className="h-3 w-3 rounded-full border border-line" />}{label}</p>
                <p className="text-xs text-muted">{m!.done ? `${m!.who ?? "Signed"} · ${fmtDate(m!.at)}` : "Not yet"}</p>
              </li>
            ))}
        </ol>
      </section>

      <section aria-labelledby="att-h" className="space-y-2">
        <h2 id="att-h" className="text-sm font-semibold">Needs attention</h2>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {strip.map((c) => (
            <li key={c.title} className="space-y-1.5 rounded-lg border border-line bg-raised px-3 py-2.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold"><span className={c.tone}><Icon name={c.icon} size={14} /></span>{c.title}<span className="ml-auto tabular-nums text-muted">{c.list.length}</span></p>
              {c.list.length === 0 ? <p className="text-xs text-muted">None</p> : c.list.map((a) => (
                <div key={a.key} className="space-y-1">
                  <p className="text-sm">{a.title}</p>
                  {a.issue && <AttentionIssueChip issue={a.issue} />}
                  <Sources sources={a.sources} />
                  <ItemAction item={a} open={open} />
                </div>
              ))}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="flow-h" className="space-y-2">
        <h2 id="flow-h" className="text-sm font-semibold">How the data flows</h2>
        <ol className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] md:items-stretch" aria-label="Inputs, work, result, feeds">
          <FlowBox title="Inputs" empty="No inputs published yet">{few(inputs)}</FlowBox>
          <Arrow />
          <FlowBox title="Work">
            <p className="text-sm">{w.steps.total ? `${w.steps.done} of ${w.steps.total} steps done` : "Steps not listed yet"}</p>
            {w.steps.total > 0 && <ProgressBar done={w.steps.done} total={w.steps.total} label="Steps done" />}
            <p className="text-xs text-muted">{w.owner ? `By ${w.owner}` : "No owner yet"}</p>
          </FlowBox>
          <Arrow />
          <FlowBox title="Result">
            <StatusPill state={w.status} label={w.statusLabel} />
            <p className="text-xs text-muted">{w.signoffs.partner.done ? "Signed by the partner" : w.signoffs.reviewer.done ? "Reviewed" : w.signoffs.preparer.done ? "Prepared, waiting for review" : "Not prepared yet"}</p>
          </FlowBox>
          <Arrow />
          <FlowBox title="Feeds" empty="Nothing depends on it">{few(feeds)}</FlowBox>
        </ol>
      </section>

      <section aria-labelledby="steps-h" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h2 id="steps-h" className="border-b border-line px-4 py-2 text-sm font-semibold">Procedure steps</h2>
        {!w.workpaperId ? <p className="px-4 py-4 text-sm text-muted">Steps appear once the workpaper is opened.</p>
          : procs.loading && !procs.data ? <LoadingRows rows={3} label="Loading steps" />
          : steps.length === 0 ? <p className="px-4 py-4 text-sm text-muted">No steps published for this workpaper.</p> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted"><tr><th className="w-10 px-3 py-1.5 font-medium">#</th><th className="px-3 py-1.5 font-medium">Step</th>
                <th className="hidden px-3 py-1.5 font-medium sm:table-cell">Done by</th><th className="px-3 py-1.5 font-medium">Status</th></tr></thead>
              <tbody className="divide-y divide-line">
                {steps.slice(0, 60).map((p, i) => (
                  <tr key={p.id} className="align-top">
                    <td className="px-3 py-1.5 text-xs tabular-nums text-muted">{i + 1}</td>
                    <td className="px-3 py-1.5"><p className="line-clamp-2">{p.text}</p>{p.status === "na" && p.naReason && <p className="text-xs text-muted">Not applicable: {p.naReason}</p>}</td>
                    <td className="hidden px-3 py-1.5 text-xs sm:table-cell">{p.performedBy ?? "—"}{p.agent && <span className="text-agent"> · agent</span>}</td>
                    <td className="px-3 py-1.5"><StatusPill state={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {steps.length > 60 && <p className="border-t border-line px-4 py-2 text-xs text-muted">Showing 60 of {steps.length}. <Link className="text-brand hover:underline" to={`/e/${ws.id}/details/wp/${w.workpaperId}`}>All steps</Link></p>}
      </section>

      <section aria-labelledby="file-h" className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-raised px-4 py-3 text-sm">
        <h2 id="file-h" className="text-sm font-semibold">Rendered file</h2>
        {url.data ? <a className="text-brand hover:underline" href={url.data} target="_blank" rel="noreferrer">Open the rendered file</a>
          : file ? <span className="text-muted">Rendered {fmtDate(file.renderedAt)} — the preview is on the engine's machine (no online copy).</span>
          : <span className="text-muted">Not rendered yet.</span>}
      </section>

      <EngineDetails>
        <p>Workpaper id <span className="font-mono">{w.workpaperId ?? "not opened"}</span>{w.template ? <> · template <span className="font-mono">{w.template}</span></> : null} · folder {w.folder}</p>
        {w.workpaperId && <p className="mt-1"><Link className="text-brand hover:underline" to={`/e/${ws.id}/details/wp/${w.workpaperId}`}>Procedures, answers, review notes and sign-off acts</Link></p>}
      </EngineDetails>
      {drawer}
    </div>
  );
}

const uniq = <T,>(xs: T[]) => [...new Set(xs)];
function Arrow() { return <li aria-hidden="true" className="hidden items-center text-muted md:flex"><Icon name="chevronRight" /></li>; }
function FlowBox({ title, children, empty }: { title: string; children: ReactNode; empty?: string }) {
  const has = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <li className="min-w-0 space-y-1.5 rounded-lg border border-line bg-raised px-3 py-2.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</p>
      {has ? children : <p className="text-xs text-muted">{empty}</p>}
    </li>
  );
}
function FlowItem({ label, to, note }: { label: string; to?: string; note?: string }) {
  return <p className="text-sm">{to ? <Link className="hover:underline" to={to}>{label}</Link> : label}{note && <span className="line-clamp-2 text-xs text-muted" title={note}>{note}</span>}</p>;
}
/** The first few items, then "and N more" (the full graph is under Engine details). */
function few(list: Array<{ label: string; to?: string; note?: string }>, n = 6) {
  const shown = list.slice(0, n).map((i, k) => <FlowItem key={k} {...i} />);
  return list.length > n ? [...shown, <p key="more" className="text-xs text-muted">and {list.length - n} more</p>] : shown;
}
